import { takeUntil, map, filter, withLatestFrom } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';
import * as i0 from '@angular/core';
import { Injector, EventEmitter, Injectable } from '@angular/core';

const FOCUSABLE_ELEMENTS_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');
/**
 * Queries an element for a list of focusable child elements.
 * Returns an array of HTMLElements, where index `0` is the first focusable child element, and index `1` is the last.
 *
 * @param HTMLElement
 * @returns the first and last focusable child elements defining the boundary of `element`.
 */
function getFocusableBoundaryElements(element) {
    const elementList = element.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
    return [
        elementList[0],
        elementList[elementList.length - 1] // The last focusable element
    ];
}
/**
 * Function that prevents focus from leaving a particular element.
 * @param HTMLElement
 */
const focusTrap = (element, stopFocusTrap = null) => {
    // Get the last focused HTMLElement as an Observable stream
    const lastFocusedElement$ = fromEvent(element, 'focusin').pipe(takeUntil(stopFocusTrap), // Stop listening on focus whenever `stopFocusTrap` emits a value
    map((event) => event.target));
    /** Respond to 'tab' and 'shift+tab' continuously via a sream */
    fromEvent(element, 'keydown')
        .pipe(takeUntil(stopFocusTrap), // Stop the focus trap whenever `stopFocusTrap` emits a value
    filter((event) => event.which === 9), // Filter out non-Tab evens
    withLatestFrom(lastFocusedElement$) // Get the currently focused element from the above observable
    )
        .subscribe(([tabEvent, focusedElement]) => {
        const [first, last] = getFocusableBoundaryElements(element);
        /* If focus is on the first focusable element inside the trap,
         * ensure the user can not Tab backwards (tab+shift) any further.
         * Instead, wrap focus around to the `last` focusable element.
         * */
        if (focusedElement === first && tabEvent.shiftKey) {
            last.focus();
            tabEvent.preventDefault();
        }
        /* If focus is on the last focusable element of the trap,
         * ensure the user can not Tab forward (tab) any further.
         * Instead, wrap focus around to the `first` focusable element.
         * */
        if (focusedElement === last && !tabEvent.shiftKey) {
            first.focus();
            tabEvent.preventDefault();
        }
    });
};

const TAB = 9;
const ENTER = 13;
const ESCAPE = 27;
const SPACE = 32;
/* HOME/END keys */
const END = 35;
const HOME = 36;
/* Arrow Keys */
const ARROW_LEFT = 37;
const ARROW_UP = 38;
const ARROW_RIGHT = 39;
const ARROW_DOWN = 40;

class ListKeyManager {
    get selected() {
        return this._selectedItem;
    }
    get selectedItemIndex() {
        return this._selectedItemIndex;
    }
    get items() {
        if (Array.isArray(this._itemsQueryList)) {
            return this._itemsQueryList;
        }
        return this._itemsQueryList.toArray();
    }
    constructor(_itemsQueryList) {
        this._itemsQueryList = _itemsQueryList;
        this._selectedItemIndex = -1;
        /**
         * Emits an event whenever a change in the selected option has been
         * made. The event emitted is the newly selected option.
         */
        this.change = new Subject();
        /**
         * Emit an event whenever the tab key is pressed, indicating that
         * focus should now exit the list.
         */
        this.tabOut = new Subject();
    }
    handleKeydown(event) {
        const keyCode = event.keyCode;
        let selectedIndex = -1;
        switch (keyCode) {
            case TAB: {
                this.tabOut.next();
                return;
            }
            case HOME: {
                this.selectFirstItem();
                return;
            }
            case END: {
                this.selectLastItem();
                return;
            }
            case ARROW_UP:
            case ARROW_LEFT: {
                if (this._selectedItemIndex === 0) {
                    this.selectLastItem();
                }
                else {
                    selectedIndex = this._selectedItemIndex - 1;
                }
                break;
            }
            case ARROW_DOWN:
            case ARROW_RIGHT: {
                if (this._selectedItemIndex === this.items.length - 1) {
                    this.selectFirstItem();
                }
                else {
                    selectedIndex = this._selectedItemIndex + 1;
                }
                break;
            }
        }
        if (selectedIndex > -1) {
            this.setSelectedItem(selectedIndex);
        }
        event.preventDefault();
    }
    setSelectedItem(item) {
        const itemArr = this.items;
        const index = this._getIndexOf(itemArr, item);
        this._selectedItem = itemArr[index];
        this._selectedItemIndex = index;
        this.change.next(this._selectedItem);
    }
    selectFirstItem() {
        this.setSelectedItem(0);
    }
    selectLastItem() {
        const lastIndex = this.items.length - 1;
        this.setSelectedItem(lastIndex);
    }
    /**
     * Find the index of a given option
     */
    _getIndexOf(items, item) {
        const index = typeof item === "number" ? item : items.indexOf(item);
        return index;
    }
}

function toggleTabIndex(element, isTabbable) {
    const tabindex = isTabbable ? '0' : '-1';
    element.setAttribute('tabindex', tabindex);
}
class TabbableOption {
    set isDisabled(isDisabled) {
        this._isDisabled = isDisabled;
        this.isFocusable = !isDisabled;
    }
    get isDisabled() {
        return this._isDisabled;
    }
    /**
     * Set whether a given element can be focused or not.
     * A given element can have focus only if its tabindex is '0' or greater; if
     * less than 0, focus cannot be set on the element.
     * @memberof TabbableOption
     */
    set isFocusable(isFocusable) {
        this._isFocusable = isFocusable;
        toggleTabIndex(this._host, isFocusable);
    }
    get isFocusable() {
        return this._isFocusable;
    }
    constructor(element) {
        this._host = element.nativeElement;
    }
    focus() {
        this._host.focus();
    }
}
class RovingTabindex {
    get selected() {
        return this._keyManager.selected;
    }
    get selectedIndex() {
        return this._keyManager.selectedItemIndex;
    }
    select(item) {
        this._keyManager.setSelectedItem(item);
    }
    constructor(_itemsQueryList) {
        this._itemsQueryList = _itemsQueryList;
        this._keyManager = new ListKeyManager(_itemsQueryList);
        this._keyManager.change.subscribe((selected) => {
            this.deselectAll();
            this._focusedEl = selected;
            this._focusedEl.isFocusable = true;
            this._focusedEl.focus();
        });
        this.change = this._keyManager.change;
        this.tabOut = this._keyManager.tabOut;
        this.handleKeyboard = this._keyManager.handleKeydown.bind(this._keyManager);
    }
    deselectAll() {
        this._itemsQueryList.forEach((option) => {
            if (this._focusedEl === option) {
                option.isFocusable = true;
            }
            {
                option.isFocusable = false;
            }
        });
    }
}

class IAlert {
}

class IButtonSelectorCarouselOptions {
}
class IButtonSelectorCarouselItem {
}
class IButtonSelectorCarouselOutputObj {
}

class IDataDisplaysCarousel {
}

class ILinkedListBlock {
}
class Item {
}
class ILinks {
}

class IScrollingTabViews {
}
class IScrollingTabView {
}
class IScrollingTabOutputObj {
}

class ITabTiles {
}
class ITab {
}

class ITabViews {
}
class ITabView {
}

class IToggleButton {
}

class IToggleSwitch {
}
class Option {
}

class TooltipDef {
    constructor() {
        this.content = '';
        this.htmlContent = '';
        this.placement = 'top';
        this.isOpen = false;
        this.enable = true;
        this.animation = true;
        this.appendToBody = false;
        this.popupClass = '';
        this.delay = 0;
        this.hasCloseBtn = true;
        this.autoFocus = true;
        this.openOnFocus = false;
    }
}

class IButtonSelectorCarouselV2 {
}

class IDefaultSelectorCarousel {
}

/**
 * This enum is used to specify CoreModule events
 */
var UxEvents;
(function (UxEvents) {
    UxEvents[UxEvents["OPEN_GLOBAL_MODAL"] = 1] = "OPEN_GLOBAL_MODAL";
    UxEvents[UxEvents["CLOSE_GLOBAL_MODAL"] = 2] = "CLOSE_GLOBAL_MODAL";
    UxEvents[UxEvents["DISMISSABLE_ALERT"] = 3] = "DISMISSABLE_ALERT";
})(UxEvents || (UxEvents = {}));

function appendNextToLocation(componentClass, location, componentFactoryResolver, providers) {
    let componentFactory = componentFactoryResolver.resolveComponentFactory(componentClass);
    let parentInjector = location.parentInjector;
    let childInjector = parentInjector;
    if (providers && providers.length > 0) {
        childInjector = Injector.create(providers, parentInjector);
    }
    return location.createComponent(componentFactory, 0, childInjector);
}

//istanbul ignore next
/**
 *  Helper provider to handle various element level functions.
 */
class UxHelper {
    constructor(applicationRef, componentFactoryResolver, injector) {
        this.applicationRef = applicationRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this._events = [];
    }
    static reflow(element) {
        new Function('bs', 'return bs')(element.offsetHeight);
    }
    // source: https://github.com/jquery/jquery/blob/master/src/css/var/getStyles.js
    static getStyles(elem) {
        // Support: IE <=11 only, Firefox <=30 (#15098, #14150)
        // IE throws on elements created in popups
        // FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
        let view = elem.ownerDocument.defaultView;
        if (!view || !view.opener) {
            view = window;
        }
        return view.getComputedStyle(elem);
    }
    getDocument() {
        return document;
    }
    /**
     * In some cases, like using ngUpgrate,
     * you need to explicitly set view container ref
     * to made this method working you need to add:
     * ```typescript
     *  @Component({
     *   selector: 'my-app',
     *   ...
     *   })
     *  export class MyApp {
     *    constructor(componentsHelper:ComponentsHelper, viewContainerRef: ViewContainerRef) {
     *        // A Default view container ref, usually the app root container ref.
     *        // Has to be set manually until we can find a way to get it automatically.
     *        componentsHelper.setRootViewContainerRef(viewContainerRef)
     *      }
     *  }
     * ```
     */
    setRootViewContainerRef(value) {
        this.root = value;
    }
    /**
     * This is a name conventional class to get application root view component ref
     * @returns - application root view component ref
     */
    getRootViewContainerRef() {
        // https://github.com/angular/angular/issues/9293
        if (this.root) {
            return this.root;
        }
        const comps = this.applicationRef.components;
        if (!comps.length) {
            throw new Error(`ApplicationRef instance not found`);
        }
        try {
            /* one more ugly hack, read issue above for details */
            const rootComponent = this.applicationRef._rootComponents[0];
            this.root = rootComponent._hostElement.vcRef;
            return this.root;
        }
        catch (e) {
            throw new Error(`ApplicationRef instance not found`);
        }
    }
    /**
     * Helper methods to add ComponentClass(like modal backdrop) with options
     * of type ComponentOptionsClass to element next to application root
     * or next to provided instance of view container
     * @param - @Component class
     * @param - options class
     * @param - instance of options
     * @returns - returns ComponentRef<T>
     */
    appendNextToRoot(componentClass, componentOptionsClass, options) {
        let location = this.getRootViewContainerRef();
        return appendNextToLocation(componentClass, location, this.componentFactoryResolver, [{ provide: componentOptionsClass, useValue: options }]);
    }
    getUxEvents(eventName) {
        if (this._events.find((e) => e.name === eventName)) {
            return this._events.find((e) => e.name === eventName).value;
        }
        let e = {
            name: eventName,
            value: new EventEmitter()
        };
        this._events.push(e);
        return e.value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UxHelper, deps: [{ token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UxHelper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UxHelper, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, END, ENTER, ESCAPE, HOME, IAlert, IButtonSelectorCarouselItem, IButtonSelectorCarouselOptions, IButtonSelectorCarouselOutputObj, IButtonSelectorCarouselV2, IDataDisplaysCarousel, IDefaultSelectorCarousel, ILinkedListBlock, ILinks, IScrollingTabOutputObj, IScrollingTabView, IScrollingTabViews, ITab, ITabTiles, ITabView, ITabViews, IToggleButton, IToggleSwitch, Item, ListKeyManager, Option, RovingTabindex, SPACE, TAB, TabbableOption, TooltipDef, UxEvents, UxHelper, appendNextToLocation, focusTrap, getFocusableBoundaryElements };
